import React, { useState, useEffect, useCallback, useRef } from 'react';
import { formatUSD, formatDate, generateTxnRef, computeKeyTest, todayISO, toUSD, getFXRate, formatFXAmount, buildMT103 } from '../utils/keyGenerator';

const ACCOUNT_TYPES = [
  { code: 'CACC', name: 'Current Account', desc: 'Standard operating account' },
  { code: 'SVGS', name: 'Savings Account', desc: 'Interest-bearing savings' },
  { code: 'TRAS', name: 'Treasury Account', desc: 'Treasury & settlement operations' },
  { code: 'COMM', name: 'Commission Account', desc: 'Fee & commission collection' },
  { code: 'CHAR', name: 'Charges Account', desc: 'Charges & expenses' },
  { code: 'LOAN', name: 'Loan Account', desc: 'Loan disbursement & repayment' },
];

const CURRENCIES = ['USD', 'EUR', 'GBP', 'JPY', 'CHF', 'AUD', 'CAD', 'THB', 'HKD', 'SGD', 'CNY', 'INR'];

const PAYMENT_METHODS = [
  { id: 'api',  label: 'Send via API',  icon: '\u25C7', desc: 'External API / Wise',             color: '#3B82F6', apiPath: '/api/send' },
  { id: 'ktt',  label: 'Send via KTT',  icon: '\u2726', desc: 'ISO 20022 SWIFT Message',         color: '#DAA520', apiPath: '/api/ktt/send' },
  { id: 'rtgs', label: 'Send via RTGS', icon: '\u2302', desc: 'Real-Time Gross Settlement',      color: '#1E3A5F', apiPath: '/api/rtgs/settle' },
  { id: 'pobo', label: 'POBO SWIFT',    icon: '\u2B21', desc: 'Payment On Behalf Of',            color: '#E74C6F', apiPath: '/api/pobo/payout' },
];

// =============================================
// ACCOUNT DASHBOARD — Full self-contained workspace
// =============================================
function AccountDashboard({ iban, onBack, allTransactions }) {
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [txFilter, setTxFilter] = useState('all');
  const [alert, setAlert] = useState(null);

  // Deposit state
  const [depAmount, setDepAmount] = useState('');
  const [depDesc, setDepDesc] = useState('');
  const [depParty, setDepParty] = useState('');
  const [depositing, setDepositing] = useState(false);
  const [duplicateWarn, setDuplicateWarn] = useState(null); // {entries, mapped, fileName, original}
  const fileRef = useRef();

  // Payment state
  const [activeMethod, setActiveMethod] = useState(null);
  const [payForm, setPayForm] = useState({
    beneficiary: '', accountNo: '', receiverBank: '', receiverBIC: '',
    currency: 'USD', amount: '', narrative: '', valueDate: todayISO(),
    mtType: 'MT103',
  });
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const pSet = k => e => setPayForm(f => ({ ...f, [k]: e.target.value }));

  const fetchAccount = useCallback(async () => {
    try {
      const data = await fetch(`/api/accounts/${iban}`).then(r => r.json());
      setAccount(data);
    } catch {} finally { setLoading(false); }
  }, [iban]);

  useEffect(() => { fetchAccount(); }, [fetchAccount]);
  useEffect(() => {
    const iv = setInterval(fetchAccount, 3000);
    return () => clearInterval(iv);
  }, [fetchAccount]);

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text3)' }}>Loading account...</div>;
  if (!account) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--coral)' }}>Account not found</div>;

  const acctTxns = allTransactions.filter(t => t.accountIban === iban);
  const credits = acctTxns.filter(t => t.creditAmount);
  const debits = acctTxns.filter(t => t.debitAmount);
  const totalCredits = credits.reduce((s, t) => s + (t.creditAmount || 0), 0);
  const totalDebits = debits.reduce((s, t) => s + (t.debitAmount || 0), 0);
  const filteredTxns = txFilter === 'credits' ? credits : txFilter === 'debits' ? debits : acctTxns;
  let runBal = 0;
  const txnsWithBal = [...filteredTxns].reverse().map(tx => {
    if (tx.creditAmount) runBal += tx.creditAmount;
    if (tx.debitAmount) runBal -= tx.debitAmount;
    return { ...tx, runningBalance: runBal };
  }).reverse();

  // ===== CORE DEPOSIT — calls backend, handles duplicate =====
  const submitDeposit = async (mapped, fileName, force) => {
    setDepositing(true);
    try {
      const res = await fetch('/api/deposit', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entries: mapped, accountIban: iban, fileName, force: !!force }),
      });
      const data = await res.json();
      if (data.duplicate) {
        // Duplicate detected — show warning with force option
        setDuplicateWarn({ entries: mapped, fileName, original: data.original, message: data.message });
        setAlert({ type: 'error', msg: 'Duplicate deposit detected — see warning below.' });
        setDepositing(false);
        return;
      }
      if (data.success) {
        const total = mapped.reduce((s, r) => s + parseFloat(r.amount), 0);
        setAlert({ type: 'success', msg: `${mapped.length} ${mapped.length === 1 ? 'entry' : 'entries'} deposited. Total: ${formatUSD(total)}` });
        setDepAmount(''); setDepDesc(''); setDepParty('');
        setDuplicateWarn(null);
        fetchAccount();
      } else {
        setAlert({ type: 'error', msg: data.error || 'Deposit failed' });
      }
    } catch (e) { setAlert({ type: 'error', msg: e.message }); }
    setDepositing(false);
  };

  // ===== DEPOSIT: Manual amount =====
  const handleDeposit = () => {
    const amt = parseFloat(depAmount);
    if (!amt || amt <= 0) { setAlert({ type: 'error', msg: 'Enter a valid deposit amount.' }); return; }
    submitDeposit([{ amount: amt, description: depDesc || 'Account Deposit', party: depParty || 'Direct Deposit' }], 'manual', false);
  };

  // ===== DEPOSIT: JSON file =====
  const handleFileDeposit = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    const text = await file.text();
    let entries;
    try {
      const raw = JSON.parse(text);
      entries = Array.isArray(raw) ? raw : raw.entries || raw.transactions || raw.data || raw.BankTransactions || [raw];
    } catch { setAlert({ type: 'error', msg: 'Invalid JSON file' }); return; }
    const mapped = entries.map(r => ({ amount: parseFloat(r.amount || r.Total || r.value || r.credit || 0), description: r.description || r.narration || r.memo || 'Ledger', party: r.party || r.sender || (r.Contact && r.Contact.Name) || 'Ledger Upload' })).filter(r => r.amount > 0);
    if (mapped.length === 0) { setAlert({ type: 'error', msg: 'No valid entries found in JSON' }); return; }
    submitDeposit(mapped, file.name, false);
  };

  // ===== FORCE DEPOSIT (override duplicate) =====
  const handleForceDeposit = () => {
    if (!duplicateWarn) return;
    submitDeposit(duplicateWarn.entries, duplicateWarn.fileName, true);
  };

  // ===== SEND PAYMENT =====
  const handleSendPayment = async () => {
    if (!payForm.beneficiary) { setAlert({ type: 'error', msg: 'Beneficiary is required.' }); return; }
    if (!payForm.accountNo) { setAlert({ type: 'error', msg: 'Account number is required.' }); return; }
    const amt = parseFloat(payForm.amount);
    if (!amt || amt <= 0) { setAlert({ type: 'error', msg: 'Enter a valid amount.' }); return; }
    // Only check balance for fund transfers — MT199/MT940 are messages only, no debit
    const isMessageOnly = activeMethod === 'ktt' && payForm.mtType !== 'MT103';
    if (!isMessageOnly) {
      const usdAmt = toUSD(amt, payForm.currency);
      if (usdAmt > account.balance) { setAlert({ type: 'error', msg: `Insufficient balance. Need ${formatUSD(usdAmt)} USD, have ${formatUSD(account.balance)}` }); return; }
    }

    setSending(true);
    const method = PAYMENT_METHODS.find(m => m.id === activeMethod);
    const txnRef = generateTxnRef(activeMethod.toUpperCase());
    const keyTest = computeKeyTest(amt, payForm.valueDate);
    let body = {};
    let resultData = null;

    try {
      if (activeMethod === 'api') {
        body = { amount: amt, currency: payForm.currency, beneficiary: payForm.beneficiary, accountNo: payForm.accountNo, bankName: payForm.receiverBank, bankBIC: payForm.receiverBIC, narrative: payForm.narrative, valueDate: payForm.valueDate, sourceAccountIban: iban };
        const res = await fetch('/api/send', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
        resultData = await res.json();
      } else if (activeMethod === 'ktt') {
        const msgBody = buildMT103({ txnRef, keyTest, ...payForm });
        body = { amount: amt, currency: payForm.currency, mtType: payForm.mtType, beneficiary: payForm.beneficiary, accountNo: payForm.accountNo, receiverBank: payForm.receiverBank, receiverBIC: payForm.receiverBIC, narrative: payForm.narrative, valueDate: payForm.valueDate, sourceAccountIban: iban, swiftMessage: msgBody };
        const res = await fetch('/api/ktt/send', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
        resultData = await res.json();
      } else if (activeMethod === 'rtgs') {
        body = { amount: amt, currency: payForm.currency, beneficiary: payForm.beneficiary, accountNo: payForm.accountNo, receiverBank: payForm.receiverBank, receiverBIC: payForm.receiverBIC, narrative: payForm.narrative, valueDate: payForm.valueDate, priority: 'NORMAL', rtgsSystem: 'BOS-RTGS', sourceAccountIban: iban };
        const res = await fetch('/api/rtgs/settle', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
        resultData = await res.json();
      } else if (activeMethod === 'pobo') {
        body = { amount: amt, currency: payForm.currency, beneficiaryName: payForm.beneficiary, beneficiaryAccount: payForm.accountNo, beneficiaryBank: payForm.receiverBank, beneficiaryBIC: payForm.receiverBIC, purposeOfPayment: payForm.narrative, valueDate: payForm.valueDate, sourceAccountIban: iban };
        const res = await fetch('/api/pobo/payout', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
        resultData = await res.json();
      }

      if (resultData && (resultData.success || resultData.transaction || resultData.settlement)) {
        const debitUSD = toUSD(amt, payForm.currency);
        setLastResult({ success: true, method: method.label, ref: resultData.transaction?.id || resultData.settlement?.settlementId || txnRef, keyTest, amount: amt, currency: payForm.currency, beneficiary: payForm.beneficiary, isMessageOnly });
        setAlert({ type: 'success', msg: isMessageOnly
          ? `${method.label} (${payForm.mtType}) message sent from ${account.ibanFormatted}. No funds debited. Ref: ${txnRef}`
          : `${method.label} sent! ${formatUSD(debitUSD)} debited from ${account.ibanFormatted}. Ref: ${txnRef}` });
        setPayForm({ beneficiary: '', accountNo: '', receiverBank: '', receiverBIC: '', currency: 'USD', amount: '', narrative: '', valueDate: todayISO(), mtType: 'MT103' });
        setActiveMethod(null);
        fetchAccount();
      } else {
        setAlert({ type: 'error', msg: resultData?.error || resultData?.reason || 'Payment failed' });
      }
    } catch (e) { setAlert({ type: 'error', msg: e.message }); }
    setSending(false);
  };

  const payKeyTest = payForm.amount && payForm.valueDate ? computeKeyTest(payForm.amount, payForm.valueDate) : '-----';

  return (
    <div className="gap-16">
      <button className="btn btn-outline btn-sm" onClick={onBack} style={{ alignSelf: 'flex-start' }}>&larr; Back to All Accounts</button>

      {/* Alert */}
      {alert && (
        <div className={`alert alert-${alert.type}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{alert.type === 'success' ? '\u2713' : '\u2717'} {alert.msg}</span>
          <button onClick={() => setAlert(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: 'inherit' }}>x</button>
        </div>
      )}

      {/* IBAN Header */}
      <div style={{ padding: '20px 24px', borderRadius: 'var(--radius)', background: 'linear-gradient(135deg, var(--navy), #1B3A5C)', color: 'white' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.2em', opacity: 0.6, marginBottom: 6 }}>ACCOUNT IBAN</div>
            <div style={{ fontSize: 24, fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace", letterSpacing: 4, color: 'var(--gold)' }}>{account.ibanFormatted}</div>
            <div style={{ display: 'flex', gap: 24, marginTop: 12, fontSize: 13, opacity: 0.9 }}>
              <span>{account.holderName}</span>
              <span>{account.typeName || account.type}</span>
              <span>{account.currency}</span>
              <span className="badge badge-settled" style={{ fontSize: 10 }}>{account.status}</span>
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.2em', opacity: 0.6, marginBottom: 6 }}>BALANCE</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--gold)' }}>{formatUSD(account.balance)}</div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid4">
        <div className="stat-tile teal"><div className="stat-label">Balance</div><div className="stat-value stat-green">{formatUSD(account.balance)}</div><div className="stat-sub">{account.currency}</div></div>
        <div className="stat-tile gold"><div className="stat-label">Total Credits</div><div className="stat-value">{formatUSD(totalCredits)}</div><div className="stat-sub">{credits.length} deposits</div></div>
        <div className="stat-tile coral"><div className="stat-label">Total Debits</div><div className="stat-value stat-red">{formatUSD(totalDebits)}</div><div className="stat-sub">{debits.length} payments</div></div>
        <div className="stat-tile blue"><div className="stat-label">Transactions</div><div className="stat-value">{acctTxns.length}</div><div className="stat-sub">All time</div></div>
      </div>

      {/* ======= DEPOSIT FUNDS — inline ======= */}
      {account.status === 'ACTIVE' && (
        <div className="card">
          <div className="card-header">
            <span className="card-title" style={{ color: 'var(--teal)' }}>{'\u25B2'} Deposit Funds</span>
            <span style={{ fontSize: 12, color: 'var(--text3)' }}>Credit to {account.ibanFormatted}</span>
          </div>
          <div className="card-body">
            <div className="grid2">
              <div className="gap-16">
                <div className="form-group">
                  <label className="form-label">Amount *</label>
                  <input className="form-input" type="number" placeholder="0.00" min="0.01" step="0.01" value={depAmount} onChange={e => setDepAmount(e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Description</label>
                  <input className="form-input" placeholder="e.g. Wire Transfer" value={depDesc} onChange={e => setDepDesc(e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">From / Party</label>
                  <input className="form-input" placeholder="e.g. Client Co Ltd" value={depParty} onChange={e => setDepParty(e.target.value)} />
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button className="btn btn-gold" onClick={handleDeposit} disabled={depositing}>{depositing ? 'Depositing...' : 'Deposit to Account'}</button>
                  <input type="file" accept=".json" ref={fileRef} style={{ display: 'none' }} onChange={handleFileDeposit} />
                  <button className="btn btn-outline" onClick={() => fileRef.current.click()} disabled={depositing}>Upload JSON Ledger</button>
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: 24, background: 'rgba(13,148,136,0.04)', borderRadius: 'var(--radius)', border: '1px dashed rgba(13,148,136,0.2)' }}>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.15em', color: 'var(--teal)', marginBottom: 12 }}>ACCOUNT TO CREDIT</div>
                <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 16, fontWeight: 700, color: 'var(--gold)', letterSpacing: 2 }}>{account.ibanFormatted}</div>
                <div style={{ fontSize: 13, color: 'var(--text2)', marginTop: 8 }}>{account.holderName}</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--teal)', marginTop: 12 }}>{formatUSD(account.balance)}</div>
              </div>
            </div>
            {/* Duplicate Warning */}
            {duplicateWarn && (
              <div style={{ marginTop: 16, padding: '16px 20px', borderRadius: 'var(--radius)', background: 'rgba(245,158,11,0.08)', border: '2px solid rgba(245,158,11,0.3)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#D97706' }}>{'\u26A0'} Duplicate Deposit Detected</div>
                  <button className="btn btn-outline btn-sm" onClick={() => setDuplicateWarn(null)} style={{ fontSize: 10, padding: '2px 8px' }}>Dismiss</button>
                </div>
                <div style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.7, marginBottom: 12 }}>
                  This exact deposit was already processed:<br />
                  <strong>Time:</strong> {new Date(duplicateWarn.original.depositedAt).toLocaleString()}<br />
                  <strong>Entries:</strong> {duplicateWarn.original.entries} | <strong>Total:</strong> ${duplicateWarn.original.totalAmount.toFixed(2)}<br />
                  {duplicateWarn.original.accountIban && <><strong>Account:</strong> {duplicateWarn.original.accountIban}<br /></>}
                  {duplicateWarn.fileName && duplicateWarn.fileName !== 'manual' && <><strong>File:</strong> {duplicateWarn.fileName}<br /></>}
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button className="btn btn-gold btn-sm" onClick={handleForceDeposit} disabled={depositing}>
                    {depositing ? 'Processing...' : 'Deposit Anyway (Override)'}
                  </button>
                  <button className="btn btn-outline btn-sm" onClick={() => setDuplicateWarn(null)}>Cancel</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ======= SELECT PAYMENT METHOD ======= */}
      {account.status === 'ACTIVE' && (
        <div className="card">
          <div className="card-header">
            <span className="card-title">Select Payment Method</span>
            {activeMethod && <button className="btn btn-outline btn-sm" onClick={() => { setActiveMethod(null); setLastResult(null); }}>Cancel</button>}
          </div>
          <div className="card-body">
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {PAYMENT_METHODS.map(m => {
                const isActive = activeMethod === m.id;
                // KTT is always enabled (MT199/MT940 don't need balance)
                const disabled = m.id !== 'ktt' && account.balance <= 0 && !isActive;
                return (
                  <button key={m.id} disabled={disabled}
                    style={{
                      flex: '1 1 160px', padding: '16px 12px', display: 'flex', flexDirection: 'column',
                      alignItems: 'center', gap: 6, minWidth: 160, cursor: disabled ? 'not-allowed' : 'pointer',
                      border: isActive ? `2.5px solid ${m.color}` : `1.5px solid ${disabled ? 'var(--border)' : m.color + '40'}`,
                      borderRadius: 'var(--radius-lg)',
                      background: isActive ? m.color + '18' : disabled ? 'var(--surface)' : 'transparent',
                      opacity: disabled ? 0.35 : 1, transition: 'all 0.15s ease',
                      boxShadow: isActive ? `0 2px 12px ${m.color}25` : 'none',
                    }}
                    onClick={() => { if (!disabled) { setActiveMethod(isActive ? null : m.id); setLastResult(null); }}}
                  >
                    <span style={{ fontSize: 26 }}>{m.icon}</span>
                    <span style={{ fontWeight: 700, fontSize: 12, color: isActive ? m.color : 'var(--navy)' }}>{m.label}</span>
                    <span style={{ fontSize: 10, color: 'var(--text3)', textAlign: 'center' }}>{m.desc}</span>
                    {disabled && <span style={{ fontSize: 9, color: 'var(--coral)', fontWeight: 600 }}>Deposit first</span>}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ======= PAYMENT FORM (inline, appears when method selected) ======= */}
      {activeMethod && (
        <div className="card" style={{ borderLeft: `4px solid ${PAYMENT_METHODS.find(m => m.id === activeMethod)?.color}` }}>
          <div className="card-header">
            <span className="card-title">{PAYMENT_METHODS.find(m => m.id === activeMethod)?.icon} {PAYMENT_METHODS.find(m => m.id === activeMethod)?.label} — Payment Details</span>
            <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, color: 'var(--gold)', fontWeight: 700, letterSpacing: '0.15em' }}>Key Test: {payKeyTest}</span>
          </div>
          <div className="card-body">
            <div style={{ padding: '10px 14px', marginBottom: 16, borderRadius: 'var(--radius)', background: 'rgba(218,165,32,0.06)', border: '1px solid rgba(218,165,32,0.15)', fontSize: 12 }}>
              Debiting from: <strong style={{ color: 'var(--gold)' }}>{account.ibanFormatted}</strong> ({account.holderName}) — Available: <strong style={{ color: 'var(--teal)' }}>{formatUSD(account.balance)}</strong>
            </div>
            <div className="grid2">
              <div className="gap-16">
                <div className="form-group"><label className="form-label">Beneficiary Name *</label><input className="form-input" placeholder="Enter beneficiary full legal name" value={payForm.beneficiary} onChange={pSet('beneficiary')} /></div>
                <div className="form-group"><label className="form-label">Account Number / IBAN *</label><input className="form-input" placeholder="Enter account number or IBAN" value={payForm.accountNo} onChange={pSet('accountNo')} /></div>
                <div className="form-group"><label className="form-label">Receiver Bank</label><input className="form-input" placeholder="Enter receiver bank name" value={payForm.receiverBank} onChange={pSet('receiverBank')} /></div>
                <div className="form-group"><label className="form-label">Receiver BIC/SWIFT</label><input className="form-input" placeholder="Enter BIC/SWIFT code" value={payForm.receiverBIC} onChange={pSet('receiverBIC')} /></div>
              </div>
              <div className="gap-16">
                <div className="grid2">
                  <div className="form-group"><label className="form-label">Currency</label>
                    <select className="form-input" value={payForm.currency} onChange={pSet('currency')}>
                      {CURRENCIES.map(c => <option key={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="form-group"><label className="form-label">Amount *</label><input className="form-input" type="number" placeholder="0.00" min="0.01" step="0.01" value={payForm.amount} onChange={pSet('amount')} /></div>
                </div>
                <div className="form-group"><label className="form-label">Value Date</label><input className="form-input" type="date" value={payForm.valueDate} onChange={pSet('valueDate')} /></div>
                {activeMethod === 'ktt' && (
                  <div className="form-group"><label className="form-label">MT Type</label>
                    <select className="form-input" value={payForm.mtType} onChange={pSet('mtType')}>
                      <option value="MT103">MT103 — Customer Credit Transfer (sends fund)</option>
                      <option value="MT199">MT199 — Free Format Message (no debit)</option>
                      <option value="MT940">MT940 — Customer Statement (no debit)</option>
                    </select>
                  </div>
                )}
                <div className="form-group"><label className="form-label">Narrative / Purpose</label><textarea className="form-input" value={payForm.narrative} onChange={pSet('narrative')} placeholder="Payment details..." /></div>
                {payForm.amount && payForm.currency !== 'USD' && (
                  <div style={{ fontSize: 12, color: 'var(--text2)', padding: '8px 12px', background: 'var(--surface2)', borderRadius: 'var(--radius)' }}>
                    FX: {formatFXAmount(parseFloat(payForm.amount), payForm.currency)} = <strong style={{ color: 'var(--gold)' }}>{formatUSD(toUSD(parseFloat(payForm.amount), payForm.currency))} USD</strong> (rate: {getFXRate(payForm.currency)})
                  </div>
                )}
                <button className="btn btn-gold btn-lg" style={{ width: '100%', marginTop: 8 }} onClick={handleSendPayment} disabled={sending}>
                  {sending ? 'Processing...' : `Confirm & Send via ${PAYMENT_METHODS.find(m => m.id === activeMethod)?.label}`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ======= LAST PAYMENT RESULT ======= */}
      {lastResult && lastResult.success && (
        <div className="card" style={{ textAlign: 'center', border: '2px solid var(--teal)' }}>
          <div className="card-body" style={{ padding: 32 }}>
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'linear-gradient(135deg, var(--teal), var(--teal-light))', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: 26, color: 'white' }}>{'\u2713'}</div>
            <h3 style={{ fontFamily: "'Source Serif 4', serif", fontSize: 20, color: 'var(--navy)' }}>{lastResult.method} Sent Successfully</h3>
            <div style={{ fontSize: 13, color: 'var(--text2)', marginTop: 8 }}>
              Ref: <strong style={{ color: 'var(--gold)' }}>{lastResult.ref}</strong> | Key Test: <strong style={{ color: 'var(--gold)' }}>{lastResult.keyTest}</strong>
            </div>
            <div style={{ fontSize: 13, color: 'var(--text2)', marginTop: 4 }}>
              {lastResult.isMessageOnly
                ? <>Message dispatched from {account.ibanFormatted} to {lastResult.beneficiary} — no funds debited</>
                : <>{formatUSD(toUSD(lastResult.amount, lastResult.currency))} debited from {account.ibanFormatted} to {lastResult.beneficiary}</>}
            </div>
          </div>
        </div>
      )}

      {/* Account Info + Summary */}
      <div className="grid2">
        <div className="card">
          <div className="card-header"><span className="card-title">Account Information</span></div>
          <div className="card-body">
            {[['IBAN', account.ibanFormatted], ['Account ID', account.accountId], ['Holder', account.holderName], ['Type', `${account.type} — ${account.typeName}`], ['Currency', account.currency], ['Bank Code', account.bankCode], ['BIC / SWIFT', account.bankBIC], ['IBAN Valid', account.ibanValid ? 'Yes (MOD 97-10)' : 'No'], ['Created', formatDate(account.createdAt)]].map(([k, v]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border-light)', fontSize: 13 }}>
                <span style={{ color: 'var(--text3)' }}>{k}</span>
                <span style={{ fontWeight: 600, fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: 'var(--gold)' }}>{v}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="card">
          <div className="card-header"><span className="card-title">Credit & Debit Summary</span></div>
          <div className="card-body">
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 12 }}><span style={{ color: 'var(--teal)', fontWeight: 700 }}>Credits</span><span style={{ color: 'var(--teal)', fontWeight: 700 }}>{formatUSD(totalCredits)}</span></div>
              <div style={{ height: 10, borderRadius: 5, background: 'var(--border)', overflow: 'hidden' }}><div style={{ height: '100%', borderRadius: 5, background: 'var(--teal)', width: totalCredits > 0 ? '100%' : '0%', transition: 'width 0.3s' }} /></div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 12 }}><span style={{ color: 'var(--coral)', fontWeight: 700 }}>Debits</span><span style={{ color: 'var(--coral)', fontWeight: 700 }}>{formatUSD(totalDebits)}</span></div>
              <div style={{ height: 10, borderRadius: 5, background: 'var(--border)', overflow: 'hidden' }}><div style={{ height: '100%', borderRadius: 5, background: 'var(--coral)', width: totalCredits > 0 ? `${Math.min(100, (totalDebits / totalCredits) * 100)}%` : '0%', transition: 'width 0.3s' }} /></div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderTop: '2px solid var(--border)', fontSize: 14 }}>
              <span style={{ fontWeight: 700 }}>Net Balance</span>
              <span style={{ fontWeight: 700, color: account.balance >= 0 ? 'var(--teal)' : 'var(--coral)', fontSize: 18 }}>{formatUSD(account.balance)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Transaction History */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">Transaction History</span>
          <div style={{ display: 'flex', gap: 6 }}>
            {[{ id: 'all', label: `All (${acctTxns.length})` }, { id: 'credits', label: `Credits (${credits.length})` }, { id: 'debits', label: `Debits (${debits.length})` }].map(f => (
              <button key={f.id} className={`btn btn-sm ${txFilter === f.id ? 'btn-gold' : 'btn-outline'}`} onClick={() => setTxFilter(f.id)}>{f.label}</button>
            ))}
          </div>
        </div>
        {filteredTxns.length === 0 ? (
          <div className="card-body" style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>No {txFilter === 'all' ? '' : txFilter} transactions yet</div>
        ) : (
          <div className="card-body" style={{ padding: 0 }}>
            <table>
              <thead><tr><th>Ref</th><th>Date</th><th>Type</th><th>Counterparty</th><th>Description</th><th style={{ textAlign: 'right' }}>Credit</th><th style={{ textAlign: 'right' }}>Debit</th><th style={{ textAlign: 'right' }}>Balance</th><th style={{ textAlign: 'center' }}>Status</th></tr></thead>
              <tbody>
                {txnsWithBal.map(tx => (
                  <tr key={tx.id}>
                    <td style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, color: 'var(--gold)' }}>{tx.id}</td>
                    <td style={{ fontSize: 11, color: 'var(--text3)', whiteSpace: 'nowrap' }}>{formatDate(tx.date)}</td>
                    <td><span className="badge badge-ktt" style={{ fontSize: 9 }}>{tx.mt || tx.type}</span></td>
                    <td style={{ fontWeight: 500 }}>{tx.counterparty}</td>
                    <td style={{ fontSize: 12, color: 'var(--text2)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{tx.description}</td>
                    <td style={{ textAlign: 'right', color: 'var(--teal)', fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace" }}>{tx.creditAmount ? `+${formatUSD(tx.creditAmount)}` : ''}</td>
                    <td style={{ textAlign: 'right', color: 'var(--coral)', fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace" }}>{tx.debitAmount ? `-${formatUSD(tx.debitAmount)}` : ''}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600, fontFamily: "'IBM Plex Mono', monospace", fontSize: 12 }}>{formatUSD(tx.runningBalance)}</td>
                    <td style={{ textAlign: 'center' }}><span className="badge badge-settled" style={{ fontSize: 9 }}>{tx.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================
// MAIN ACCOUNTS PAGE — List + Create
// =============================================
export default function AccountsPage({ onRefresh, allTransactions }) {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ holderName: '', currency: 'USD', type: 'CACC', description: '' });
  const [creating, setCreating] = useState(false);
  const [alert, setAlert] = useState(null);
  const [openAccountIban, setOpenAccountIban] = useState(null);
  const [validateIban, setValidateIban] = useState('');
  const [validateResult, setValidateResult] = useState(null);

  const fetchAccounts = useCallback(async () => {
    try {
      const data = await fetch('/api/accounts').then(r => r.json());
      setAccounts(data);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);
  useEffect(() => {
    const interval = setInterval(fetchAccounts, 5000);
    return () => clearInterval(interval);
  }, [fetchAccounts]);

  const handleCreate = async () => {
    if (!form.holderName.trim()) {
      setAlert({ type: 'error', msg: 'Account holder name is required' });
      return;
    }
    setCreating(true);
    setAlert(null);
    try {
      const res = await fetch('/api/accounts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (data.success) {
        setAlert({
          type: 'success',
          msg: `Account created: ${data.account.ibanFormatted} — ${data.account.holderName} (${data.account.typeName})`,
        });
        setForm({ holderName: '', currency: 'USD', type: 'CACC', description: '' });
        setShowCreate(false);
        fetchAccounts();
        if (onRefresh) onRefresh();
      } else {
        setAlert({ type: 'error', msg: data.error || 'Failed to create account' });
      }
    } catch (err) {
      setAlert({ type: 'error', msg: err.message });
    } finally { setCreating(false); }
  };

  const handleValidateIban = async () => {
    if (!validateIban.trim()) return;
    try {
      const res = await fetch('/api/accounts/validate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ iban: validateIban }),
      });
      setValidateResult(await res.json());
    } catch {}
  };

  // If an account is open, show its full dashboard
  if (openAccountIban) {
    return (
      <AccountDashboard
        iban={openAccountIban}
        onBack={() => setOpenAccountIban(null)}
        allTransactions={allTransactions || []}
      />
    );
  }

  const totalBalance = accounts.reduce((sum, a) => sum + a.balance, 0);

  return (
    <div className="gap-16">
      {/* Alert */}
      {alert && (
        <div className={`alert ${alert.type === 'error' ? 'alert-error' : 'alert-success'}`}>
          {alert.msg}
          <button onClick={() => setAlert(null)} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', fontSize: 16 }}>x</button>
        </div>
      )}

      {/* Stats */}
      <div className="grid4">
        <div className="stat-tile teal">
          <div className="stat-label">Total Accounts</div>
          <div className="stat-value">{accounts.length}</div>
          <div className="stat-sub">Active</div>
        </div>
        <div className="stat-tile gold">
          <div className="stat-label">Combined Balance</div>
          <div className="stat-value">{formatUSD(totalBalance)}</div>
          <div className="stat-sub">All accounts</div>
        </div>
        <div className="stat-tile blue">
          <div className="stat-label">Currencies</div>
          <div className="stat-value">{new Set(accounts.map(a => a.currency)).size || 0}</div>
          <div className="stat-sub">In use</div>
        </div>
        <div className="stat-tile coral">
          <div className="stat-label">Bank Code</div>
          <div className="stat-value" style={{ fontSize: 18 }}>BSAI</div>
          <div className="stat-sub">BIC: BSAIMPM1XXX</div>
        </div>
      </div>

      {/* Action */}
      <div style={{ display: 'flex', gap: 12 }}>
        <button className="btn btn-gold" onClick={() => setShowCreate(!showCreate)}>
          {showCreate ? 'Cancel' : '+ Create New Account'}
        </button>
      </div>

      {/* Create Form */}
      {showCreate && (
        <div className="card">
          <div className="card-header">
            <span className="card-title">Open New Account — ISO 13616 IBAN Standard</span>
          </div>
          <div className="card-body">
            <div className="grid2">
              <div>
                <label className="form-label">Account Holder Name *</label>
                <input className="form-input" placeholder="e.g. Account holder legal name"
                  value={form.holderName} onChange={e => setForm(f => ({ ...f, holderName: e.target.value }))}
                  onKeyDown={e => e.key === 'Enter' && handleCreate()} />
              </div>
              <div>
                <label className="form-label">Currency</label>
                <select className="form-input" value={form.currency}
                  onChange={e => setForm(f => ({ ...f, currency: e.target.value }))}>
                  {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="form-label">Account Type (ISO 20022)</label>
                <select className="form-input" value={form.type}
                  onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
                  {ACCOUNT_TYPES.map(t => (
                    <option key={t.code} value={t.code}>{t.code} — {t.name}</option>
                  ))}
                </select>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>
                  {ACCOUNT_TYPES.find(t => t.code === form.type)?.desc}
                </div>
              </div>
              <div>
                <label className="form-label">Description (optional)</label>
                <input className="form-input" placeholder="e.g. Main operating account"
                  value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
              </div>
            </div>

            <div style={{
              marginTop: 16, padding: '12px 16px', borderRadius: 'var(--radius)',
              background: 'rgba(218,165,32,0.06)', border: '1px solid rgba(218,165,32,0.15)',
            }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--gold)', letterSpacing: '0.15em', marginBottom: 6 }}>
                IBAN GENERATION STANDARD
              </div>
              <div style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.6 }}>
                Account number will be auto-generated following <strong>ISO 13616 IBAN</strong> format with{' '}
                <strong>ISO 7064 MOD 97-10</strong> check digits.<br />
                Format: <code style={{ color: 'var(--gold)' }}>MP[check] BSAI [currency][sequence]</code><br />
                Country: MP | Bank Code: BSAI | BIC: BSAIMPM1XXX
              </div>
            </div>

            <div style={{ marginTop: 16, display: 'flex', gap: 10 }}>
              <button className="btn btn-gold" onClick={handleCreate} disabled={creating}>
                {creating ? 'Creating...' : 'Create Account & Generate IBAN'}
              </button>
              <button className="btn btn-outline" onClick={() => setShowCreate(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Accounts Table */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">Accounts Register</span>
          <span style={{ fontSize: 11, color: 'var(--text3)' }}>{accounts.length} accounts — click to open dashboard</span>
        </div>
        {loading ? (
          <div className="card-body" style={{ textAlign: 'center', color: 'var(--text3)' }}>Loading...</div>
        ) : accounts.length === 0 ? (
          <div className="card-body" style={{ textAlign: 'center', padding: 40 }}>
            <div style={{ fontSize: 14, color: 'var(--text3)', marginBottom: 12 }}>No accounts yet</div>
            <button className="btn btn-gold" onClick={() => setShowCreate(true)}>Create First Account</button>
          </div>
        ) : (
          <div className="card-body" style={{ padding: 0 }}>
            <table>
              <thead>
                <tr>
                  <th>IBAN</th>
                  <th>Holder</th>
                  <th>Type</th>
                  <th>Currency</th>
                  <th style={{ textAlign: 'right' }}>Balance</th>
                  <th style={{ textAlign: 'center' }}>Txns</th>
                  <th style={{ textAlign: 'center' }}>Status</th>
                  <th>Created</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {accounts.map(acct => (
                  <tr key={acct.iban} style={{ cursor: 'pointer' }} onClick={() => setOpenAccountIban(acct.iban)}>
                    <td style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: 'var(--gold)', fontWeight: 600, letterSpacing: 1 }}>
                      {acct.ibanFormatted}
                    </td>
                    <td style={{ fontWeight: 600 }}>{acct.holderName}</td>
                    <td><span className="badge badge-ktt" style={{ fontSize: 10 }}>{acct.type}</span></td>
                    <td style={{ fontWeight: 600 }}>{acct.currency}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: acct.balance > 0 ? 'var(--teal)' : 'var(--text2)' }}>
                      {formatUSD(acct.balance)}
                    </td>
                    <td style={{ textAlign: 'center' }}>{acct.transactionCount}</td>
                    <td style={{ textAlign: 'center' }}>
                      <span className={`badge ${acct.status === 'ACTIVE' ? 'badge-settled' : acct.status === 'FROZEN' ? 'badge-pending' : 'badge-failed'}`}
                        style={{ fontSize: 10 }}>{acct.status}</span>
                    </td>
                    <td style={{ fontSize: 11, color: 'var(--text3)' }}>{formatDate(acct.createdAt)}</td>
                    <td>
                      <button className="btn btn-gold btn-sm" onClick={(e) => { e.stopPropagation(); setOpenAccountIban(acct.iban); }}>
                        Dashboard
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* IBAN Validator */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">IBAN Validator — ISO 7064 MOD 97-10</span>
        </div>
        <div className="card-body">
          <div style={{ display: 'flex', gap: 10 }}>
            <input className="form-input" style={{ flex: 1, fontFamily: "'IBM Plex Mono', monospace", letterSpacing: 2 }}
              placeholder="Enter IBAN to validate"
              value={validateIban} onChange={e => setValidateIban(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleValidateIban()} />
            <button className="btn btn-outline" onClick={handleValidateIban}>Validate</button>
          </div>
          {validateResult && (
            <div style={{
              marginTop: 10, padding: '10px 14px', borderRadius: 'var(--radius)',
              background: validateResult.valid ? 'rgba(5,150,105,0.06)' : 'rgba(239,68,68,0.06)',
              border: `1px solid ${validateResult.valid ? 'rgba(5,150,105,0.2)' : 'rgba(239,68,68,0.2)'}`,
            }}>
              <span style={{ fontWeight: 700, color: validateResult.valid ? 'var(--teal)' : 'var(--coral)' }}>
                {validateResult.valid ? 'VALID' : 'INVALID'} IBAN
              </span>
              {validateResult.internal && (
                <span style={{ marginLeft: 12, fontSize: 11, color: 'var(--gold)' }}>
                  Internal account — {validateResult.account?.holderName}
                </span>
              )}
              <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4, fontFamily: "'IBM Plex Mono', monospace" }}>
                {validateResult.iban} | Standard: {validateResult.standard}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
